/**
 * The offline write queue, as a value: what a queued write is, what order the
 * queue drains in, and what each possible answer means.
 *
 * **The case this exists for is a restaurant basement.** Somebody types an
 * expense on a phone with no signal. The screen must not say "saved" — that is
 * a lie about a server that has never heard of it — and it must not say
 * "failed" either, because nothing has failed and re-typing it upstairs is the
 * exact thing an app should be for. It says **queued**, the write goes into
 * IndexedDB, and it is sent when there is a network. `src/queue.ts` is the
 * machine that does that; what is here is every decision it makes.
 *
 * ## Exactly once
 *
 * A queue cannot tell "the request never arrived" from "the reply never came
 * back", so it must retry, and a retry must not file a second expense. The
 * whole of the answer is one rule, and it is the reason this file mints the
 * key rather than the sender:
 *
 *   **the `Idempotency-Key` is minted when the write is queued, stored
 *   alongside it, and reused unchanged on every attempt for the rest of its
 *   life.**
 *
 * Mint it per *attempt* and the server has no way to recognise the second one.
 * Mint it at enqueue and `server/idempotency.ts` holds up its end: the key's
 * row is written in the same `d1.batch()` as the expense, so a reply that was
 * lost after the write landed is replayed byte-for-byte — including its `201`
 * — to the retry that follows. Two devices draining the same queue at once is
 * the same case and gets the same answer.
 *
 * ## What each answer means
 *
 * `classify` is the other half. A queue that retried everything forever would
 * hammer the server with a request it has already refused four times; a queue
 * that dropped anything it could not send would silently lose an expense
 * somebody typed. So there are four outcomes, not two, and only one of them
 * takes the write out of the queue quietly.
 */

import { formatMoney } from './format'

/** The methods a write can be. `GET` is not a write and is never queued. */
export type QueuedMethod = 'POST' | 'PATCH' | 'DELETE'

/**
 * What the queued write *is*, in the words the banner uses. It is a copy of
 * the parts of the body a person would recognise, not a re-parse of the body:
 * "1 expense waiting" is a sentence the queue has to be able to say without
 * understanding the API.
 */
export type QueuedWhat = {
  /** `expense`, `settlement`, `correction` — what the person did, not the route. */
  kind: 'expense' | 'settlement' | 'correction'
  description: string
  amountCents: number
  currency: string
  groupId: string
}

/** A write waiting for a network, exactly as it is stored. */
export type QueuedWrite = {
  /** Unique, and stable across attempts. `${seq}` is what it drains in. */
  id: string
  /** Assigned at enqueue and never changed: the order these were typed in. */
  seq: number
  /** The `Idempotency-Key`. Minted once, here, and never re-minted. See above. */
  key: string
  method: QueuedMethod
  /** Path only, including any query string. The origin is wherever we are. */
  path: string
  /** The exact bytes to send, or null for a body-less write like a delete. */
  body: string | null
  what: QueuedWhat
  /** ISO, for "queued at 19:42" and for nothing the server ever reads. */
  queuedAt: string
  attempts: number
  /** Epoch ms before which this must not be attempted again. Backoff. */
  notBefore: number
  /**
   * Set when the server refused this for good. The write stays in the queue —
   * visible, with the reason — because an expense somebody typed is not ours
   * to throw away silently. It is dropped when they say so.
   */
  refusal: { code: string; message: string } | null
}

// ---------------------------------------------------------------------------
// What an answer means
// ---------------------------------------------------------------------------

export type Attempt =
  /** It landed — or it had already landed and the server replayed its answer. */
  | { outcome: 'sent' }
  /** Nothing is wrong that waiting will not fix. Try again later. */
  | { outcome: 'retry'; because: string }
  /** The server has decided. Retrying is asking the same question again. */
  | { outcome: 'refused'; code: string; message: string }
  /** Nobody is signed in. Keep the write and stop draining until somebody is. */
  | { outcome: 'blocked' }

/**
 * What the API's answer means for the write that provoked it.
 *
 * The interesting line is the last one. A `409` from `server/idempotency.ts`
 * means this key was used for a *different* request, which is a bug in this
 * client and not something a network will fix; a `409` from the transaction
 * routes means the group is archived or the row is deleted, which is equally
 * settled. Either way the honest thing is to stop and say so, next to the
 * expense it is about, rather than to keep trying and keep quiet.
 */
export function classify(status: number, code?: string): Attempt {
  if (status >= 200 && status < 300) return { outcome: 'sent' }
  if (status === 401) return { outcome: 'blocked' }
  if (status === 408 || status === 429 || status >= 500) {
    return { outcome: 'retry', because: `the server answered ${status}` }
  }
  return {
    outcome: 'refused',
    code: code ?? `http_${status}`,
    message: `the server refused this (${code ?? status})`,
  }
}

/**
 * A `fetch` that threw. Every one of these is "there is no network right now"
 * — a DNS failure, a dropped connection, a captive portal, aeroplane mode —
 * and every one of them is fixed by waiting, which is the whole premise.
 */
export function classifyThrow(err: unknown): Attempt {
  return {
    outcome: 'retry',
    because: err instanceof Error && err.message ? err.message : 'there is no network',
  }
}

// ---------------------------------------------------------------------------
// When to try again
// ---------------------------------------------------------------------------

/** First retry, and the ceiling. Doubling in between. */
export const RETRY_BASE_MS = 2_000
export const RETRY_MAX_MS = 5 * 60_000

/**
 * How long to wait after `attempts` failures.
 *
 * No jitter, deliberately: this is one phone's own queue, not a thundering
 * herd, and a deterministic backoff is one a test can assert on. Coming back
 * online resets the wait outright (`readyAt` below is compared against the
 * clock, and `src/queue.ts` clears it on `online`) — the phone leaving the
 * basement is new information, and making somebody wait five minutes after it
 * would be the queue holding a grudge.
 */
export function backoffMs(attempts: number): number {
  if (attempts <= 0) return 0
  return Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** (attempts - 1))
}

/** The queue in the order it drains: oldest first, always. */
export function inOrder(writes: readonly QueuedWrite[]): QueuedWrite[] {
  return [...writes].sort((a, b) => a.seq - b.seq || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/**
 * The next write to attempt, or nothing.
 *
 * **Strictly one at a time, strictly in order, and a refusal stops the queue.**
 * The writes in here are not independent: a correction to an expense is behind
 * the expense it corrects, and sending them out of order — or sending the
 * second after the first was refused — asks the server to patch something that
 * does not exist. So the head of the queue is the only candidate there ever
 * is, and a write parked on a refusal parks everything behind it until
 * somebody deals with it.
 */
export function nextDue(writes: readonly QueuedWrite[], now: number): QueuedWrite | null {
  const head = inOrder(writes)[0]
  if (!head) return null
  if (head.refusal !== null) return null
  return head.notBefore <= now ? head : null
}

/** The write after a failed attempt: one more try, and a longer wait. */
export function afterAttempt(write: QueuedWrite, attempt: Attempt, now: number): QueuedWrite {
  const attempts = write.attempts + 1
  if (attempt.outcome === 'refused') {
    return { ...write, attempts, refusal: { code: attempt.code, message: attempt.message } }
  }
  // `blocked` waits for a session rather than for a timer, and the timer would
  // be a guess about when somebody signs in. It goes to the back of the
  // shortest wait there is and the drain stops; see `src/queue.ts`.
  const wait = attempt.outcome === 'blocked' ? RETRY_BASE_MS : backoffMs(attempts)
  return { ...write, attempts, notBefore: now + wait }
}

// ---------------------------------------------------------------------------
// What the screen says
// ---------------------------------------------------------------------------

/** The queue as the banner reads it. */
export type QueueSummary = {
  /** Everything still to send, refusals included. */
  total: number
  /** The ones that will go by themselves. */
  waiting: number
  /** The ones that will not, and need somebody to look. */
  refused: number
}

export function summarise(writes: readonly QueuedWrite[]): QueueSummary {
  const refused = writes.filter((write) => write.refusal !== null).length
  return { total: writes.length, waiting: writes.length - refused, refused }
}

/**
 * One queued write, as a line somebody can recognise: "Ramen · $18.40".
 *
 * The money goes through `formatMoney` like every other number in this app,
 * because a queued expense is the one an amount is most likely to be checked
 * against — it is the thing somebody is being asked to believe has not been
 * lost.
 */
export function describeWrite(write: QueuedWrite): string {
  return `${write.what.description} · ${formatMoney(write.what.amountCents, write.what.currency)}`
}

/**
 * The sentence at the top of the screen. It has to be true in four states and
 * short in all of them, and it never says "saved".
 */
export function describeQueue(summary: QueueSummary, online: boolean): string {
  if (summary.total === 0) {
    return online ? '' : "You're offline. Anything you file will be sent when you're back."
  }
  const things = (n: number) => `${n} ${n === 1 ? 'write' : 'writes'}`
  if (summary.refused === summary.total) {
    return `${things(summary.refused)} couldn't be filed.`
  }
  const queued = online
    ? `${things(summary.waiting)} still sending.`
    : `You're offline. ${things(summary.waiting)} queued — they'll be sent when you're back.`
  return summary.refused === 0
    ? queued
    : `${queued} ${things(summary.refused)} couldn't be filed.`
}
